import {
    Column,
    CompareFn,
    inverseSortOrder,
    Row,
    Sort,
    SortOrder,
} from './experimental.grid';
import { TableState } from './tdcf-form.state';
import {
    HierarchyInfo,
    Mode,
    PayloadView,
    PayloadViewModelSetTransformer,
    ReviewDateRange,
    TeamViewModelTransformer,
    UserView,
    UserViewModelSetTransformer,
} from './tdcf.model';

export enum ColumnID {
    QUARTER,
    LAST_MODIFIED,
    STATUS,
    NAME,
    FORM,
}

export enum ColumnType {
    QUARTER,
    LAST_MODIFIED,
    STATUS,
    NAME,
}

export enum RowType {
    HEADER,
    ENTRY,
    USER,
    FORM,
}

function compareQuarterName(which: 'quarter' | 'name'): CompareFn {
    return (cellA, cellB, rowA, rowB, def): number => {
        const retA =
            cellA.context.ctx == null
                ? ''
                : cellA.context.ctx[which].toLocaleLowerCase();
        const retB =
            cellB.context.ctx == null
                ? ''
                : cellB.context.ctx[which].toLocaleLowerCase();
        return def.order === SortOrder.ASC
            ? retA.localeCompare(retB)
            : retB.localeCompare(retA);
    };
}

function compareLastModified(cellA, cellB, rowA, rowB, def): number {
    const retA =
        cellA.context.ctx == null || cellA.context.ctx.lastModified == null
            ? Number.MIN_SAFE_INTEGER
            : (cellA.context.ctx.lastModified as Date).getTime();
    const retB =
        cellB.context.ctx == null || cellB.context.ctx.lastModified == null
            ? Number.MIN_SAFE_INTEGER
            : (cellB.context.ctx.lastModified as Date).getTime();
    return def.order === SortOrder.ASC ? retA - retB : retB - retA;
}

function compareStatus(cellA, cellB, rowA, rowB, def): number {
    const retA =
        cellA.context.ctx == null
            ? Number.MIN_SAFE_INTEGER
            : cellA.context.ctx.status;
    const retB =
        cellB.context.ctx == null
            ? Number.MIN_SAFE_INTEGER
            : cellB.context.ctx.status;
    return def.order === SortOrder.ASC ? retA - retB : retB - retA;
}

export class DataSource {
    thTpl: any; // TemplateRef
    tdTpl: any; // TemplateRef
    tdFormTpl: any; // TemplateRef
    noDataTpl: any; // TemplateRef
    state: TableState;

    private _rowIdCounter: number;
    private _mode: Mode;
    private _reviewDateRanges: ReviewDateRange[];
    private _hierarchy: HierarchyInfo;
    columns: Column[];
    headerRows: Row[];
    lastSort: Sort;

    constructor() {
        this._rowIdCounter = 0;
    }

    private _setDataMember(): void {
        this.columns = [
            {
                id: ColumnID.QUARTER.toString(),
                type: ColumnType.QUARTER,
                width: null,
                compareFn: compareQuarterName('quarter'),
            },
            {
                id: ColumnID.LAST_MODIFIED.toString(),
                type: ColumnType.LAST_MODIFIED,
                width: null,
                compareFn: compareLastModified,
            },
            {
                id: ColumnID.STATUS.toString(),
                type: ColumnType.STATUS,
                width: null,
                compareFn: compareStatus,
            },
        ];

        this.headerRows = [
            {
                cells: [
                    {
                        tpl: this.thTpl,
                        columnId: ColumnID.QUARTER.toString(),
                        columnType: ColumnType.QUARTER,
                    },
                    {
                        tpl: this.thTpl,
                        columnId: ColumnID.LAST_MODIFIED.toString(),
                        columnType: ColumnType.LAST_MODIFIED,
                    },
                    {
                        tpl: this.thTpl,
                        columnId: ColumnID.STATUS.toString(),
                        columnType: ColumnType.STATUS,
                    },
                ],
                id: null,
                type: RowType.HEADER,
            },
        ];
    }

    private _setDataManager(): void {
        this.columns = [
            {
                id: ColumnID.NAME.toString(),
                type: ColumnType.NAME,
                width: null,
                compareFn: compareQuarterName('name'),
            },
            {
                id: ColumnID.LAST_MODIFIED.toString(),
                type: ColumnType.LAST_MODIFIED,
                width: null,
                compareFn: compareLastModified,
            },
            {
                id: ColumnID.STATUS.toString(),
                type: ColumnType.STATUS,
                width: null,
                compareFn: compareStatus,
            },
        ];

        this.headerRows = [
            {
                cells: [
                    {
                        tpl: this.thTpl,
                        columnId: ColumnID.NAME.toString(),
                        columnType: ColumnType.NAME,
                    },
                    {
                        tpl: this.thTpl,
                        columnId: ColumnID.LAST_MODIFIED.toString(),
                        columnType: ColumnType.LAST_MODIFIED,
                    },
                    {
                        tpl: this.thTpl,
                        columnId: ColumnID.STATUS.toString(),
                        columnType: ColumnType.STATUS,
                    },
                ],
                id: null,
                type: RowType.HEADER,
            },
        ];
    }

    setData(
        mode: Mode,
        reviewDateRanges: ReviewDateRange[],
        hierarchy: HierarchyInfo,
    ): void {
        this._mode = mode;
        this._reviewDateRanges = reviewDateRanges;
        this._hierarchy = hierarchy;

        const fn =
            mode === Mode.MEMBER ? this._setDataMember : this._setDataManager;
        fn.call(this);
    }

    private _makeEntryRows(data: PayloadView[], level: number): Row[] {
        return data.map((value) => ({
            ctx: { level, expanded: false },
            cells: [
                {
                    tpl: this.tdTpl,
                    ctx: value,
                    columnId: (this._mode === Mode.MEMBER
                        ? ColumnID.QUARTER
                        : ColumnID.NAME
                    ).toString(),
                    columnType: ColumnType.QUARTER,
                },
                {
                    tpl: this.tdTpl,
                    ctx: value,
                    columnId: ColumnID.LAST_MODIFIED.toString(),
                    columnType: ColumnType.LAST_MODIFIED,
                },
                {
                    tpl: this.tdTpl,
                    ctx: value,
                    columnId: ColumnID.STATUS.toString(),
                    columnType: ColumnType.STATUS,
                },
            ],
            id: (this._rowIdCounter++).toString(),
            type: RowType.ENTRY,
        }));
    }

    private _makeFormRow(data: PayloadView, level: number): Row {
        return {
            ctx: { level, expanded: false },
            cells: [
                {
                    tpl: this.tdFormTpl,
                    ctx: data,
                    columnId: ColumnID.FORM.toString(),
                    columnType: null,
                },
            ],
            id: (this._rowIdCounter++).toString(),
            type: RowType.FORM,
        };
    }

    private _makeUserRows(data: UserView[], level: number): Row[] {
        return data.map((value) => ({
            ctx: { level, expanded: false },
            cells: [
                {
                    tpl: this.tdTpl,
                    ctx: value,
                    columnId: ColumnID.NAME.toString(),
                    columnType: ColumnType.NAME,
                },
                {
                    tpl: this.tdTpl,
                    ctx: value,
                    columnId: ColumnID.LAST_MODIFIED.toString(),
                    columnType: ColumnType.LAST_MODIFIED,
                },
                {
                    tpl: this.tdTpl,
                    ctx: value,
                    columnId: ColumnID.STATUS.toString(),
                    columnType: ColumnType.STATUS,
                },
            ],
            id: (this._rowIdCounter++).toString(),
            type: RowType.USER,
        }));
    }

    private _makeNoDataRow(level: number): Row {
        return {
            ctx: { level, expanded: false },
            cells: [
                {
                    tpl: this.noDataTpl,
                    columnId: ColumnID.FORM.toString(),
                    columnType: null,
                },
            ],
            id: (this._rowIdCounter++).toString(),
            type: null,
        };
    }

    makeRows(row?: Row): Row[] {
        const type = !row ? null : row.type;
        const level = !row ? -1 : row.ctx.level;

        if (
            !this.state ||
            (this._mode === Mode.MEMBER && !this.state.me) ||
            (this._mode === Mode.MANAGER && !this.state.team)
        ) {
            return [];
        }

        let rows;
        if (this._mode === Mode.MEMBER) {
            const me = new PayloadViewModelSetTransformer(
                this._mode,
                this._reviewDateRanges,
            ).transform(this.state.me);
            if (type === RowType.ENTRY) {
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                rows = [this._makeFormRow(me[row.id], level + 1)];
            } else {
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                rows = this._makeEntryRows(me, level + 1);
            }
        } else {
            const team = new TeamViewModelTransformer(
                this._hierarchy,
            ).transform(
                new PayloadViewModelSetTransformer(
                    this._mode,
                    this._reviewDateRanges,
                ).transform(this.state.team),
            );
            switch (type) {
                case RowType.ENTRY: {
                    const data = team
                        .find(
                            (m) => m.info.userId === row.cells[0].ctx.memberId,
                        )
                        .payloads.find(
                            (p) => p.dataId === row.cells[0].ctx.dataId,
                        );
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    rows = [this._makeFormRow(data, level + 1)];
                    break;
                }
                case RowType.USER: {
                    const data = team.find(
                        (m) => m.info.userId === row.cells[0].ctx.info.userId,
                    ).payloads;
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    rows = this._makeEntryRows(data, level + 1);
                    break;
                }
                default: {
                    const data = new UserViewModelSetTransformer(
                        this._mode,
                        this._reviewDateRanges,
                    ).transform(team);
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    rows = this._makeUserRows(data, level + 1);
                }
            }
        }

        if (rows.length === 0) {
            rows.push(this._makeNoDataRow(0));
        }

        return rows;
    }

    sortDefs(column: Column): Sort[] {
        const opts = inverseSortOrder(
            column,
            this.lastSort,
            column && column.id === ColumnID.LAST_MODIFIED.toString()
                ? SortOrder.DESC
                : SortOrder.ASC,
        );
        if (!opts) return;
        this.lastSort = opts;
        const sortDefs = [
            this._mode === Mode.MEMBER
                ? {
                      columnId: ColumnID.LAST_MODIFIED.toString(),
                      order: this.lastSort.order,
                  }
                : {
                      columnId: ColumnID.NAME.toString(),
                      order: this.lastSort.order,
                  },
        ];
        if (
            this.lastSort.columnId !== ColumnID.LAST_MODIFIED.toString() ||
            this.lastSort.columnId !== ColumnID.NAME.toString()
        ) {
            sortDefs.splice(0, 1, {
                columnId: this.lastSort.columnId,
                order: this.lastSort.order,
            });
        }
        return sortDefs;
    }
}
